import {
  Avatar,
  Box,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { forwardRef, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export interface DataBarsRow {
  title: ReactNode | null;
  value: ReactNode | null;
  sortValue?: number;
  valueDelta?: number;
  formatValueDelta?: (value: number) => ReactNode;
  percent: number;
  percentDelta?: number;
  tooltip?: ReactNode | null;
  emoji: string | null;
  onClick?: () => void;
  barClassName?: string;
  centered?: boolean;
}

export interface DataBarsProps {
  rows: DataBarsRow[];
  className?: string;
  onClick?: () => void;
}

export const DataBars = forwardRef<HTMLDivElement, DataBarsProps>(
  ({ rows, className, onClick }, ref) => {
    const renderBar = (row: DataBarsRow) => {
      const barColorClass = row.barClassName ?? "bg-brand-dark/60";
      const barHoverClass = row.onClick ? "cursor-pointer" : "";

      if (row.centered) {
        const absPercent = Math.abs(row.percent);
        const width = row.percent === 0 ? "1px" : `${absPercent}%`;

        return (
          <Stack
            className={`h-2.5 w-full group bg-dark/5 overflow-hidden rounded-[2px] ${barHoverClass}`}
            direction={"horizontal"}
            onClick={(e) => {
              e.stopPropagation();
              row.onClick?.();
            }}
          >
            <Box className={"h-2.5 w-1/2 flex justify-end"}>
              {row.percent <= 0 && (
                <Box style={{ width }} className={`h-2.5 ${barColorClass} duration-75`} />
              )}
            </Box>

            <Box className={"h-2.5 w-1/2"}>
              {row.percent >= 0 && (
                <Box style={{ width }} className={`h-2.5 ${barColorClass} duration-75`} />
              )}
            </Box>
          </Stack>
        );
      }

      return (
        <Stack
          className={`h-2.5 w-full group bg-dark/5 overflow-hidden rounded-[2px] ${barHoverClass} relative`}
          direction={"horizontal"}
          onClick={(e) => {
            e.stopPropagation();
            row.onClick?.();
          }}
        >
          <Box
            style={{
              width: `${row.percent - (row.percentDelta && row.percentDelta > 0 ? row.percentDelta : 0)}%`,
            }}
            className={`h-2.5 ${barColorClass} duration-75`}
          />
          {row.percentDelta && row.percentDelta > 0 ? (
            <Box
              className={`h-2.5 bg-positive absolute top-0 opacity-40`}
              style={{ width: `${row.percentDelta}%`, left: `${row.percent - row.percentDelta}%` }}
            />
          ) : undefined}
          {row.percentDelta && row.percentDelta < 0 ? (
            <Box
              className={`h-2.5 bg-danger absolute top-0 opacity-40`}
              style={{ width: `${-row.percentDelta}%`, left: `${row.percent}%` }}
            />
          ) : undefined}
        </Stack>
      );
    };

    const renderBarWithTooltip = (row: DataBarsRow) => {
      const bar = renderBar(row);

      if (!row.tooltip) return bar;

      return (
        <Tooltip>
          <TooltipTrigger>{bar}</TooltipTrigger>
          <TooltipContent>{row.tooltip}</TooltipContent>
        </Tooltip>
      );
    };

    return (
      <Stack ref={ref} gap={"lg"} className={className ?? "py-(--gap-md)"} onClick={onClick}>
        {rows.map((row, index) => (
          <Stack gap={"xs"} key={`option-${index}`}>
            <Stack direction={"horizontal"} justify={"between"} items={"center"} gap={"md"}>
              <Stack
                direction={"horizontal"}
                gap={"xs"}
                items={"center"}
                className={"font-semibold text-ellipsis"}
              >
                {row.emoji && <Avatar emoji={row.emoji} size={"sm"} variant={"plain"} />}
                {row.title ?? ""}
              </Stack>

              <Stack gap={"sm"} direction={"horizontal"} items={"center"}>
                <Text className={""}>{row.value}</Text>
                {row.valueDelta !== undefined && (
                  <Text
                    className={twMerge(
                      row.valueDelta > 0 ? "text-positive" : "text-danger",
                      row.valueDelta == 0 ? "text-default" : "",
                      "text-sm min-w-8 text-right"
                    )}
                  >
                    {row.formatValueDelta
                      ? row.formatValueDelta(row.valueDelta)
                      : `${row.valueDelta > 0 ? "+" : ""}${row.valueDelta}`}
                  </Text>
                )}
              </Stack>
            </Stack>

            {renderBarWithTooltip(row)}
          </Stack>
        ))}
      </Stack>
    );
  }
);
